"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import { gsap } from "@/lib/gsap";
import styles from "./TimelineClient.module.css";
import AnimatedImage from "../AnimatedImage";

interface TimelineItemProps {
  step: string;
  title: string;
  description: string;
  image?: {
    src: string;
    alt: string;
  };
}

export default function TimelineItem({
  step,
  title,
  description,
  image,
}: TimelineItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    let lastScrollY = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const currentScrollY = window.scrollY;
          const isScrollingDown = currentScrollY > lastScrollY;

          if (entry.isIntersecting) {
            
            if (isScrollingDown) {
              entry.target.classList.add("is-visible");
            } else {
              
              entry.target.classList.add("is-visible");
            }
          } else {
            
            
            if (!isScrollingDown && entry.intersectionRatio < 0.05) {
              entry.target.classList.remove("is-visible");
            }
          }

          lastScrollY = currentScrollY;
        });
      },
      { threshold: 0.1 }
    );

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  
  useEffect(() => {
    if (!maskRef.current || !image) return;

    const mask = maskRef.current;
    const imageElement = mask.querySelector("img");

    if (!imageElement) return;

    
    const animationSettings = {
      duration: 2.5, 
      imageStartPosition: 150, 
      imageScale: 1.6, 
      maskStartPosition: 50, 
      ease: "power2.out", 
    };

    
    const ctx = gsap.context(() => {
      
      gsap.set(mask, {
        xPercent: animationSettings.maskStartPosition,
        autoAlpha: 1,
      });
      gsap.set(imageElement, {
        xPercent: animationSettings.imageStartPosition,
        scale: animationSettings.imageScale,
      });
    }, mask);

    
    let lastScrollY = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const currentScrollY = window.scrollY;
          const isScrollingDown = currentScrollY > lastScrollY;

          if (entry.isIntersecting) {
            
            if (isScrollingDown) {
              
              gsap.set(mask, {
                xPercent: animationSettings.maskStartPosition,
              });
              gsap.set(imageElement, {
                xPercent: animationSettings.imageStartPosition,
                scale: animationSettings.imageScale,
              });

              
              gsap.to(imageElement, animationSettings.duration, {
                xPercent: 0,
                scale: 1,
                ease: animationSettings.ease,
              });
              gsap.to(mask, animationSettings.duration, {
                xPercent: 0,
                ease: animationSettings.ease,
              });
            } else {
              
              gsap.set(mask, {
                xPercent: 0,
              });
              gsap.set(imageElement, {
                xPercent: 0,
                scale: 1,
              });
            }
          } else {
            
            
            if (!isScrollingDown && entry.intersectionRatio < 0.05) {
              gsap.set(mask, {
                xPercent: animationSettings.maskStartPosition,
              });
              gsap.set(imageElement, {
                xPercent: animationSettings.imageStartPosition,
                scale: animationSettings.imageScale,
              });
            }
          }

          lastScrollY = currentScrollY;
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0] }
    );

    
    observer.observe(mask);

    
    return () => {
      ctx.revert();
      observer.disconnect();
    };
  }, [image]);

  return (
    <div
      ref={ref}
      className="pl-10 relative mb-16 timeline-item scroll-into-view group "
    >
      <div className={`${styles["timeline-dot"]} timeline-dot`}></div>
      <div className={`${styles["timeline-content"]} timeline-content`}>
        <p className={`${styles["timeline-step"]} timeline-step`}>{step}</p>
        <h3 className={`${styles["timeline-title"]} timeline-title`}>
          {title}
        </h3>
        <p className={`${styles["timeline-description"]} timeline-description`}>
          {description}
        </p>
      </div>
      {image && image.src && (
        <AnimatedImage
          className={`${styles.mask} mask mt-4 relative w-full h-[265px] max-sm:h-[300px] sm:h-[368px] rounded-lg overflow-hidden`}
          animationSettings={{
            duration: 0.8,
            imageStartPosition: 130,
            imageScale: 1,
            maskStartPosition: -70,
            ease: "power2.out",
            threshold: 0.5,
          }}
        >
          <Image
            src={image.src}
            alt={image.alt}
            fill
            quality={85}
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
            className="object-cover object-center"
          />
        </AnimatedImage>
      )}
    </div>
  );
}
